import { intersectionBy, orderBy } from 'lodash';

import type { LevelSetCardItem, Skill, SkillMasteryLevel } from 'bundles/levelsets/levelsets-v2/types';
import { getSupportedSkills } from 'bundles/levelsets/levelsets-v2/utils/supportedSkills';

/** Provides the data we need to pass to each <LevelSetCard />, in the desired order. */
export const getLevelSetCardItems = (
  programSkills: Skill[],
  currentUserSkillMasteryLevels: SkillMasteryLevel[],
  pinnedSkills: string[]
): LevelSetCardItem[] => {
  // Filter the program skills down to only the ones we support for LevelSets 2.0:
  const supportedSkills = getSupportedSkills();
  const supportedProgramSkills = intersectionBy(programSkills, supportedSkills, 'skillId');

  // Join each skill with the learner's current mastery level in it (if any) to create the card items:
  const levelSetCardItems = supportedProgramSkills.map((skill) => {
    const { masteryLevel, masteryPercent } =
      currentUserSkillMasteryLevels.find((skillMasteryLevel) => skillMasteryLevel.skillId === skill.skillId) || {};
    return { id: skill.skillId, skill, masteryLevel, masteryPercent };
  });

  const orderByColumns: ((item: LevelSetCardItem) => string | number)[] = [];
  const orderByDirections: ('asc' | 'desc')[] = [];

  // Show skills where you achieved mastery first, starting with your highest mastery:
  orderByColumns.push((item: LevelSetCardItem) => item.masteryLevel || 'MASTERY_LEVEL_0');
  orderByDirections.push('desc');

  // Next place pinned skills in order:
  orderByColumns.push((item: LevelSetCardItem) =>
    pinnedSkills.includes(item.skill.skillId) ? pinnedSkills.indexOf(item.skill.skillId) : Number.MAX_SAFE_INTEGER
  );
  orderByDirections.push('asc');

  // Finally, sort by skill name:
  orderByColumns.push((item: LevelSetCardItem) => item.skill.skillName);
  orderByDirections.push('asc');

  return orderBy(levelSetCardItems, orderByColumns, orderByDirections);
};
